import React from "react";
import type { UseFormReturn } from "react-hook-form";

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import RequiredMark from "@/components/ui/required-mark";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

/**
 * Type for grouped material options returned from the API
 */
type EnclosureMaterialGroup = {
  group: string;
  options: Array<{
    id: number;
    material: string;
    type: string;
  }>;
};

/**
 * Props for the FlangeClampForm component
 */
interface FlangeClampFormProps {
  /**
   * React Hook Form instance for managing form state
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>;
  /**
   * Grouped flange clamp options fetched from the API
   */
  flangeClampOptions?: EnclosureMaterialGroup[];
  /**
   * Grouped stud bolt options fetched from the API
   */
  studBoltOptions?: Array<{
    group: string;
    options: Array<{
      id: number;
      material: string;
      type: string;
    }>;
  }>;
}

/**
 * FlangeClampForm Component
 *
 * Renders the form fields specific to flange clamp repair type.
 * Includes:
 * - Flange Clamp material selection (grouped by type: Bar, Plate, etc.)
 * - Stud Bolts material selection (grouped)
 *
 * @example
 * ```tsx
 * <FlangeClampForm
 *   form={form}
 *   flangeClampOptions={data.flangeClampOptions}
 *   studBoltOptions={data.studBoltOptions}
 * />
 * ```
 */
export function FlangeClampForm({ form, flangeClampOptions, studBoltOptions }: FlangeClampFormProps) {
  return (
    <>
      <div className="flex-1 basis-full md:basis-56">
        <FormField
          control={form.control}
          name="flange_clamp"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Flange Clamp
                <RequiredMark />
              </FormLabel>
              <FormControl>
                <Select value={String(field.value)} onValueChange={field.onChange}>
                  <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                    <SelectValue placeholder="Select flange clamp type" />
                  </SelectTrigger>
                  <SelectContent>
                    {flangeClampOptions?.map((group, groupIndex) => (
                      <React.Fragment key={group.group}>
                        {groupIndex > 0 && <SelectSeparator />}
                        <SelectGroup>
                          <SelectLabel>{group.group}</SelectLabel>
                          {group.options.map((option) => (
                            <SelectItem key={option.id} value={String(option.id)}>
                              {option.material} - {option.type}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="flex-1 basis-full md:basis-56">
        <FormField
          control={form.control}
          name="stud_bolts"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>
                  Stud Bolts
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <Select value={String(field.value)} onValueChange={field.onChange}>
                    <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                      <SelectValue placeholder="Select stud bolt type" />
                    </SelectTrigger>
                    <SelectContent>
                      {studBoltOptions?.map((group, groupIndex) => (
                        <React.Fragment key={group.group}>
                          {groupIndex > 0 && <SelectSeparator />}
                          <SelectGroup>
                            <SelectLabel>{group.group}</SelectLabel>
                            {group.options.map((option) => (
                              <SelectItem key={option.id} value={String(option.id)}>
                                {option.material} - {option.type}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </div>
    </>
  );
}
